import { useState, useRef, useId, type JSX, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

interface TooltipProps {
  /** The tooltip's text. Pass undefined/empty to render children with no tooltip wrapper overhead. */
  content?: string | undefined
  position?: TooltipPosition
  children: ReactNode
  /** Applied to the wrapping span — use to control layout (e.g. `inline-flex`, `w-full`). */
  className?: string
}

const GAP = 6

function computePosition(rect: DOMRect, position: TooltipPosition): { top: number; left: number } {
  switch (position) {
    case 'top':
      return { top: rect.top - GAP, left: rect.left + rect.width / 2 }
    case 'bottom':
      return { top: rect.bottom + GAP, left: rect.left + rect.width / 2 }
    case 'left':
      return { top: rect.top + rect.height / 2, left: rect.left - GAP }
    case 'right':
      return { top: rect.top + rect.height / 2, left: rect.right + GAP }
  }
}

const TRANSFORM_BY_POSITION: Record<TooltipPosition, string> = {
  top: 'translate(-50%, -100%)',
  bottom: 'translate(-50%, 0)',
  left: 'translate(-100%, -50%)',
  right: 'translate(0, -50%)',
}

const ARROW_CLASSES: Record<TooltipPosition, string> = {
  top: 'top-full left-1/2 -translate-x-1/2 -mt-[3px] border-t-[#16273F] border-x-transparent border-b-transparent',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 -mb-[3px] border-b-[#16273F] border-x-transparent border-t-transparent',
  left: 'left-full top-1/2 -translate-y-1/2 -ml-[3px] border-l-[#16273F] border-y-transparent border-r-transparent',
  right: 'right-full top-1/2 -translate-y-1/2 -mr-[3px] border-r-[#16273F] border-y-transparent border-l-transparent',
}

/**
 * Shared, brand-styled tooltip — replaces the browser's plain native
 * `title="..."` attribute (inconsistent styling/delay across OSes) with a
 * small dark rounded popover matching the app's popover/menu visual system
 * (rounded-[3px], shadow-xl, #16273F/gray-900 dark surface).
 *
 * Rendered through a portal into document.body and positioned via the
 * trigger's viewport rect (not CSS `absolute` relative to an inline
 * wrapper), so it's never clipped by an ancestor's `overflow: hidden` or
 * trapped under a header/sidebar's stacking context — both real problems
 * for a tooltip anchored inside a scrollable tab strip.
 *
 * Usage: wrap the trigger element — `<Tooltip content="Refresh"><button .../></Tooltip>`.
 */
export default function Tooltip({ content, position = 'top', children, className = '' }: TooltipProps): JSX.Element {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const id = useId()

  if (!content) return <>{children}</>

  const show = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect) setCoords(computePosition(rect, position))
  }
  const hide = (): void => setCoords(null)

  return (
    <span
      ref={triggerRef}
      className={`inline-flex ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-describedby={coords ? id : undefined}
    >
      {children}
      {coords &&
        createPortal(
          <span
            role="tooltip"
            id={id}
            style={{ top: coords.top, left: coords.left, transform: TRANSFORM_BY_POSITION[position] }}
            className="pointer-events-none fixed z-[100] whitespace-nowrap rounded-[3px] bg-[#16273F] px-2 py-1 text-[11.5px] font-medium text-white shadow-xl shadow-black/10"
          >
            {content}
            <span className={`absolute h-0 w-0 border-[4px] ${ARROW_CLASSES[position]}`} />
          </span>,
          document.body,
        )}
    </span>
  )
}
